import { styled } from 'styled-components'

import type { ContractDto } from '@b-stock/contract-api-client'
import { BidStatus } from '@b-stock/fe-core/bstock-next/queries'
import { Tag, TagType, FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Typography } from '@b-stock/fe-core/design-system'
import type { OrderResponseDto } from '@b-stock/order-api-client'

/** temporary enum for BuyNow order status */
export enum BuyNowStatus {
  BOUGHT = 'BOUGHT',
  SOLD = 'SOLD',
}

const StyledTag = styled(Tag)`
  ${Typography.Subtitle3}
  margin-left: 2rem;
  margin-bottom: 0.5rem;

  @media ${Breakpoints.max.medium} {
    margin-left: 1rem;
  }
`

const isOrderCanceled = (order?: OrderResponseDto | null) => {
  return order?.closure?.reason && order.closure.reason !== 'COMPLETED'
}

const getTagType = (
  bidStatus: BidStatus | BuyNowStatus,
  order?: OrderResponseDto | null
): TagType => {
  if (
    bidStatus === BuyNowStatus.SOLD ||
    bidStatus === BidStatus.LOSING ||
    bidStatus === BidStatus.LOST ||
    isOrderCanceled(order)
  ) {
    return TagType.error
  }
  return TagType.success
}

const getMessageKey = (
  bidStatus: BidStatus | BuyNowStatus,
  isAuctionCanceled: boolean,
  order?: OrderResponseDto | null,
  contract?: ContractDto | null
) => {
  if (bidStatus === BidStatus.WON || bidStatus === BuyNowStatus.BOUGHT) {
    if (isAuctionCanceled) {
      return 'WON_AND_CANCELED'
    }

    if (contract) {
      // Winning a contract listing doesn't produce an order but rather
      // an executed contract
      return 'WON_CONTRACT_CREATED'
    } else if (order) {
      // Indicate if an order has been created yet (there's a small window of
      // time between winning an auction and order creation) and if the order
      // is canceled or not
      if (bidStatus === BuyNowStatus.BOUGHT) {
        // Slightly different copy for buy-it-now vs. winning by bid
        return isOrderCanceled(order)
          ? 'BOUGHT_ORDER_CANCELED'
          : 'BOUGHT_AND_CREATED'
      } else {
        return isOrderCanceled(order) ? 'WON_ORDER_CANCELED' : 'WON_AND_CREATED'
      }
    }
  }
  return bidStatus
}

const StatusTag = ({
  bidStatus,
  className,
  order,
  contract,
}: {
  bidStatus: BidStatus | BuyNowStatus
  className?: string
  order?: OrderResponseDto | null
  contract?: ContractDto | null
}) => {
  if (bidStatus === BidStatus.NO_BID) return null
  return (
    <StyledTag type={getTagType(bidStatus, order)} className={className}>
      <FormattedMessage
        id={`Auction.details.tag.${getMessageKey(bidStatus, false, order, contract)}`}
      />
    </StyledTag>
  )
}

export default StatusTag
